import { supabase } from "@/integrations/supabase/client";
import { getShopifyEmbeddedContext, getShopifyEmbeddedIdToken } from "@/lib/shopifyEmbedded";

const SHOPIFY_SESSION_TOKEN_HEADER = "X-Shopify-Session-Token";
const SHOPIFY_EMBEDDED_HEADER = "X-Shopify-Embedded";
const SHOPIFY_BRIDGE_WAIT_MS = 1500;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim() || "";

interface InvokeFunctionOptions {
  body?: unknown;
  headers?: Record<string, string>;
}

interface FetchFunctionOptions {
  body?: BodyInit | Record<string, unknown> | unknown[] | null;
  headers?: Record<string, string>;
  method?: string;
}

function isEmbeddedShopifyContext(): boolean {
  return getShopifyEmbeddedContext().embedded;
}

async function getShopifySessionToken(): Promise<string | null> {
  if (typeof window === "undefined" || !isEmbeddedShopifyContext()) {
    return null;
  }

  const urlToken = new URLSearchParams(window.location.search).get("id_token")?.trim() || null;
  const storedToken = getShopifyEmbeddedIdToken();
  const launchToken = urlToken || storedToken;

  // The launch URL already contains Shopify's signed App Bridge ID token.
  // Prefer it during initial registration so a transient App Bridge bridge
  // failure cannot block account creation.
  if (launchToken) {
    return launchToken;
  }

  // Do not call Shopify App Bridge here. The launch token is the signed
  // credential used by the registration endpoints and remains available on
  // the embedded route/session storage.
  return launchToken;
}

async function waitForShopifyGlobal(): Promise<ShopifyAppBridgeGlobal | undefined> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < SHOPIFY_BRIDGE_WAIT_MS) {
    if (window.shopify?.idToken) {
      return window.shopify;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }

  return window.shopify;
}

export async function getShopifySessionHeaders(
  headers: Record<string, string> = {},
): Promise<Record<string, string>> {
  const suppliedSessionToken = Object.entries(headers).find(
    ([key, value]) => key.toLowerCase() === SHOPIFY_SESSION_TOKEN_HEADER.toLowerCase() && value.trim(),
  )?.[1];

  if (suppliedSessionToken) {
    return {
      ...headers,
      [SHOPIFY_EMBEDDED_HEADER]: "1",
    };
  }

  if (!isEmbeddedShopifyContext()) {
    return headers;
  }

  const sessionToken = await getShopifySessionToken();

  return {
    ...headers,
    [SHOPIFY_EMBEDDED_HEADER]: "1",
    ...(sessionToken ? { [SHOPIFY_SESSION_TOKEN_HEADER]: sessionToken } : {}),
  };
}

async function getSupabaseAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token?.trim() || "";

  if (!isCurrentProjectSupabaseToken(accessToken)) {
    return null;
  }

  return accessToken;
}

export async function getSupabaseFunctionHeaders(
  headers: Record<string, string> = {},
): Promise<Record<string, string>> {
  const sessionHeaders = await getShopifySessionHeaders(headers);
  const accessToken = await getSupabaseAccessToken();

  if (!accessToken) {
    return {
      ...sessionHeaders,
      ...(SUPABASE_PUBLISHABLE_KEY ? {
        apikey: sessionHeaders.apikey || SUPABASE_PUBLISHABLE_KEY,
        Authorization: sessionHeaders.Authorization || `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      } : {}),
    };
  }

  return {
    ...sessionHeaders,
    ...(SUPABASE_PUBLISHABLE_KEY ? { apikey: sessionHeaders.apikey || SUPABASE_PUBLISHABLE_KEY } : {}),
    Authorization: sessionHeaders.Authorization || `Bearer ${accessToken}`,
  };
}

export async function fetchSupabaseFunction(
  functionName: string,
  options: FetchFunctionOptions = {},
): Promise<Response> {
  const method = options.method || "POST";
  const headers = await getSupabaseFunctionHeaders(options.headers);

  let body = options.body;
  const hasJsonContentType = Object.keys(headers).some(
    (key) => key.toLowerCase() === "content-type",
  );

  if (
    body !== undefined &&
    body !== null &&
    typeof body !== "string" &&
    !(body instanceof FormData) &&
    !(body instanceof URLSearchParams) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer)
  ) {
    body = JSON.stringify(body);
    if (!hasJsonContentType) {
      headers["Content-Type"] = "application/json";
    }
  }

  return fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`, {
    method,
    headers,
    body: body ?? undefined,
  });
}

export async function invokeShopifyEmbeddedFunction<T = unknown>(
  functionName: string,
  options: InvokeFunctionOptions = {},
) {
  const headers = await getShopifySessionHeaders(options.headers);

  return supabase.functions.invoke<T>(functionName, {
    body: options.body,
    headers,
  });
}

export { SHOPIFY_EMBEDDED_HEADER, SHOPIFY_SESSION_TOKEN_HEADER };

function isCurrentProjectSupabaseToken(token: string): boolean {
  if (!token) {
    return false;
  }

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload !== "object") {
    return false;
  }

  const currentProjectRef = getCurrentSupabaseProjectRef();
  if (!currentProjectRef) {
    return true;
  }

  const payloadRef = typeof payload.ref === "string" ? payload.ref.trim() : "";
  if (payloadRef) {
    return payloadRef === currentProjectRef;
  }

  const issuer = typeof payload.iss === "string" ? payload.iss.trim() : "";
  if (issuer) {
    try {
      const issuerHost = new URL(issuer).hostname;
      return issuerHost.startsWith(`${currentProjectRef}.`);
    } catch {
      if (issuer.includes(currentProjectRef)) {
        return true;
      }
    }
  }

  // Some authenticated Supabase JWTs do not include a `ref` claim.
  // In that case, trust authenticated-role tokens from the current client
  // instead of downgrading requests to anon and breaking protected APIs.
  return payload.role === "authenticated";
}

function getCurrentSupabaseProjectRef(): string | null {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
  if (!supabaseUrl) {
    return null;
  }

  try {
    return new URL(supabaseUrl).hostname.split(".")[0] || null;
  } catch (error) {
    console.error("Failed to parse Supabase project ref from URL:", error);
    return null;
  }
}

function decodeJwtPayload(token: string): Record<string, any> | null {
  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`;
    return JSON.parse(window.atob(padded));
  } catch (error) {
    console.error("Failed to decode JWT payload:", error);
    return null;
  }
}
